import * as R from 'ramda';
import { useFormik } from 'formik';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
// components
import { FormFooter } from '../../../../components/form-footer';
// forms
import { Fieldset2 } from '../../../../forms';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
import {
  DEFAULT_WEIGHT_RATE_UNIT_OPTIONS,
  DEFAULT_DISTANCE_RATE_UNIT_OPTIONS,
} from '../../../../helpers/options';
// ui
import { Box, Flex, scrollableContainerCss4px } from '../../../../ui';
// utilities
import { sendRequest } from '../../../../utilities/http';
import endpointsMap from '../../../../utilities/endpoints';
// feature carrier-profile
import { setAddressPointValues } from '../../helpers';
import SectionHeader from '../../components/section-header';
import FormGroupTable from '../../components/form-group-table';
import { AccessorialFormGroupTable } from '../../settings/accessorial';
import {
  awardedLaneVariableDiscountSettings,
  awardedLaneFreightClassOverrideSettings,
  smc3AwardedLaneVariableDiscountSettings,
} from '../../settings';
import {
  defaultValues,
  validationSchema,
  zoneFieldSettings,
  generalFieldSettings,
  compensationRangeSections,
} from './settings';
//////////////////////////////////////////////////

const defaultFieldsWrapperStyles = { px: '0px', pt: 15, width: 980, justifyContent: 'space-between' };

const ZoneSection = (props) => (
  <Box
    width={480}
    p='15px 10px'
    borderRadius='8px'
    border='1px solid'
    borderColor='dark.blue'
  >
    <Box ml={10} mb={25} fontWeight='bold' color='light.blue'>
      {props.title}
    </Box>
    <Fieldset2
      {...props}
      fields={zoneFieldSettings(props.zoneType)}
      setValues={props.handleSetAddressPointValues}
      fieldsWrapperStyles={{ justifyContent: 'space-between' }}
    />
  </Box>
);

const getActionButtons = ({ isSubmitting, initialValues, setSaveAndAddNew }) => {
  if (G.isNotNil(G.getGuidFromObject(initialValues))) return [];

  return [
    {
      type: 'submit',
      disabled: isSubmitting,
      action: () => setSaveAndAddNew(true),
      displayText: G.getWindowLocale('titles:save-and-add-new', 'Save and Add New'),
      buttonStyles: {
        mr: 20,
        ml: 'auto',
        width: 'max-content',
      },
    },
  ];
};

const useForm = (props) => {
  const {
    openLoader,
    closeLoader,
    submitAction,
    initialValues,
    saveAndAddNew,
  } = props;

  const [entity, setEntity] = useState(initialValues);

  useEffect(() => {
    const getAwardedLaneRequest = async () => {
      const guid = G.getGuidFromObject(initialValues);

      if (R.isNil(guid)) return;

      openLoader();

      const endpoint = endpointsMap.getCarrierAwardedLineEndpoint(guid);

      const res = await sendRequest('get', endpoint);

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        setEntity(data);
      } else {
        G.handleFailResponseSimple(res, true, 'getAwardedLaneRequest fail');
      }

      closeLoader();
    };

    getAwardedLaneRequest();
  }, []);

  const onSubmit = useCallback((values, { resetForm, setSubmitting }) => {
    const makeLocationValues = R.compose(
      G.mapObjectEmptyStringFieldsToNull,
      R.pick([
        GC.FIELD_GEO_FENCING_ZONE_STATES,
        GC.FIELD_GEO_FENCING_ZONE_CITIES,
        GC.FIELD_GEO_FENCING_ZONE_ZIP_CODES,
        GC.FIELD_GEO_FENCING_ZONE_COUNTRIES,
        GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_TO,
        GC.FIELD_GEO_FENCING_ZONE_ZIP_RANGE_FROM,
      ]),
    );

    const origin = makeLocationValues(values.origin);
    const destination = makeLocationValues(values.destination);

    let data = {
      ...G.mapObjectEmptyStringFieldsToNull(R.omit(['origins', 'destinations'], values)),
      origin,
      destination,
    };

    const discountType = R.path([GC.FIELD_DISCOUNT, GC.FIELD_DISCOUNT_TYPE], values);

    if (G.notEquals(discountType, GC.DISCOUNT_TYPE_VARIABLE)) {
      data = R.assocPath(
        [GC.FIELD_DISCOUNT, GC.FIELD_PAYROLL_VARIABLE_VALUES],
        null,
        data,
      );
    }

    if (G.isNilOrEmpty(discountType)) data = R.assoc(GC.FIELD_DISCOUNT, null, data);

    const failCallback = () => setSubmitting(false);

    const successCallback = () => {
      setSubmitting(false);

      if (saveAndAddNew) resetForm();
    };

    const additionalOptions = {
      failCallback,
      successCallback,
      shouldCloseModal: G.isFalse(saveAndAddNew),
    };

    submitAction(data, additionalOptions);
  }, [saveAndAddNew]);

  const formik = useFormik({
    onSubmit,
    validationSchema,
    enableReinitialize: true,
    initialValues: G.setInitialFormikValues(
      defaultValues,
      entity,
    ),
  });

  return { formik, initialValues: entity };
};

const AwarderLaneForm = (props) => {
  const [saveAndAddNew, setSaveAndAddNew] = useState(false);

  const { isSmc3 } = props;

  const form = useForm({ ...props, saveAndAddNew });

  const { formik, initialValues } = form;

  const { values, setValues, handleSubmit, isSubmitting } = formik;

  const handleSetAddressPointValues = useCallback(
    (entity) => setAddressPointValues({ entity, values, setValues }),
    [values],
  );

  const awardedLineGuid = R.path(['initialValues', GC.FIELD_GUID], props);
  const variableType = R.path([GC.FIELD_CHARGE_DISCOUNT, GC.FIELD_VARIABLE_TYPE], values);
  const isSmc3VariableWeightDiscount = R.and(isSmc3, R.equals(variableType, GC.CHARGE_RATE_TYPE_WEIGHT));

  const variableUnitOptions = useMemo(() => {
    const map = {
      [GC.CHARGE_RATE_TYPE_WEIGHT]: DEFAULT_WEIGHT_RATE_UNIT_OPTIONS,
      [GC.CHARGE_RATE_TYPE_DISTANCE]: DEFAULT_DISTANCE_RATE_UNIT_OPTIONS,
    };

    return R.propOr([], variableType, map);
  }, [variableType]);

  const variableValues = R.pathOr([], [GC.CHARGE_TYPE_DISCOUNT, GC.FIELD_PAYROLL_VARIABLE_VALUES], values);

  return (
    <form onSubmit={handleSubmit}>
      <Box
        px='6px'
        overflowY='auto'
        overflowX='hidden'
        maxHeight='calc(100vh - 180px)'
        css={scrollableContainerCss4px}
      >
        <Fieldset2
          {...formik}
          fields={generalFieldSettings}
          fieldsWrapperStyles={defaultFieldsWrapperStyles}
        />
        <Flex mb={15} alignItems='stretch' justifyContent='space-between'>
          <ZoneSection
            {...formik}
            zoneType={GC.FIELD_ORIGIN}
            title={G.getWindowLocale('titles:origin', 'Origin')}
            handleSetAddressPointValues={handleSetAddressPointValues}
          />
          <ZoneSection
            {...formik}
            zoneType={GC.FIELD_DESTINATION}
            handleSetAddressPointValues={handleSetAddressPointValues}
            title={G.getWindowLocale('titles:destination', 'Destination')}
          />
        </Flex>
        {
          compensationRangeSections.map(({ title, fields, prefix, fieldSettings }, index) => (
            <SectionHeader
              key={index}
              title={G.getWindowLocale(title)}
              expanded={G.isOneNotNilOrNotEmpty(
                R.values(R.pick(fields, R.propOr(initialValues, prefix, initialValues))),
              )}
            >
              <Fieldset2
                {...formik}
                fields={fieldSettings}
                variableUnitOptions={variableUnitOptions}
                fieldsWrapperStyles={defaultFieldsWrapperStyles}
              />
            </SectionHeader>
          ))
        }
        {
          R.pathEq(GC.DISCOUNT_TYPE_VARIABLE, [GC.CHARGE_TYPE_DISCOUNT, GC.FIELD_DISCOUNT_TYPE], values) &&
          <FormGroupTable
            {...props}
            isReport={false}
            totalCount={null}
            setValues={setValues}
            itemList={variableValues}
            group={G.ifElse(
              isSmc3VariableWeightDiscount,
              smc3AwardedLaneVariableDiscountSettings,
              awardedLaneVariableDiscountSettings,
            )}
          />
        }
        {
          isSmc3 &&
          <FormGroupTable
            {...props}
            isReport={false}
            totalCount={null}
            setValues={setValues}
            group={awardedLaneFreightClassOverrideSettings}
            itemList={R.propOr([], GC.FIELD_FREIGHT_CLASS_OVERRIDES, values)}
          />
        }
        {
          R.isNotNil(awardedLineGuid) &&
          <AccessorialFormGroupTable
            {...props}
            isReport={false}
            totalCount={null}
            primaryObjectGuid={awardedLineGuid}
            groupName={`awardedLine.${awardedLineGuid}.accessorial`}
          />
        }
      </Box>
      <FormFooter
        submitDisabled={isSubmitting}
        boxStyles={{ mt: 10, px: '5px' }}
        submitAction={() => setSaveAndAddNew(false)}
        actionButtons={getActionButtons({ ...props, setSaveAndAddNew })}
      />
    </form>
  );
};

export default AwarderLaneForm;
